import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { Box, CircularProgress } from '@mui/material';
import { db } from './services/firebase';
import theme from './styles/theme';

// Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import SingleMode from './components/game/SingleMode';
import DuoMode from './components/game/DuoMode';
import AuthLayout from './components/layouts/AuthLayout';
import DashboardLayout from './components/layouts/DashboardLayout';
import AdminLogin from './components/admin/AdminLogin';
import AdminPanel from './components/admin/AdminPanel';
import PremiumPackage from './components/PremiumPackage';
import Home from './components/Home';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        if (userData?.paymentStatus === 'completed' && 
            userData?.subscriptionType === 'premium' && 
            userData?.subscriptionEndDate?.toDate() > new Date()) {
          setHasAccess(true);
        } else {
          navigate('/premium-package');
        }
      } catch (error) {
        console.error('Ödeme durumu kontrolü hatası:', error);
        navigate('/premium-package');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [currentUser, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return null;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Ana Sayfa */}
          <Route path="/" element={<Home />} />

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/premium-package" element={<PremiumPackage />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminPanel />} />

          {/* Protected Routes */}
          <Route element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/play/single" element={<SingleMode />} />
            <Route path="/play/duo" element={<DuoMode />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
